import React from 'react';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Typography,
} from 'components/mui';
import { ExpandMore } from 'components/icons';
import { PurchasesByEmail } from 'api/lotusBlossom/lotusBlossomAPI';
import ParticipantsGrid from './ParticipantsGrid';
import { daysUntil, formatSessionRange, ThemeSession } from '../libs/themeLists';

interface ComingUpSessionsProps {
	/** Sessions after the one shown above, soonest first. */
	sessions: ThemeSession[];
	subscribersListId: number | null;
	/** From the page's single Stripe read; null while it is still loading. */
	purchases: PurchasesByEmail | null;
}

function countdown(session: ThemeSession): string {
	const days = daysUntil(session);
	if (days <= 0) return 'Happening now';
	if (days === 1) return 'Tomorrow';
	return `In ${days} days`;
}

/**
 * The sessions after the one at the top of the tab, one line each. A roster
 * loads only when its session is opened, so the list costs nothing until used.
 */
function ComingUpSessions({ sessions, subscribersListId, purchases }: ComingUpSessionsProps) {
	if (sessions.length === 0) return null;

	return (
		<Box sx={{ mt: 4 }}>
			<Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
				Coming up
			</Typography>

			{sessions.map((session) => (
				<Accordion
					key={session.listId}
					disableGutters
					// Unmounted while closed, so its roster is fetched on open and
					// not for every session on the list.
					TransitionProps={{ unmountOnExit: true }}
				>
					<AccordionSummary
						expandIcon={<ExpandMore />}
						sx={{
							'& .MuiAccordionSummary-expandIconWrapper': {
								alignSelf: { xs: 'flex-start', md: 'center' },
								mt: { xs: 1.5, md: 0 },
							},
						}}
					>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: {
									xs: '1fr auto',
									md: 'minmax(180px, max-content) 1fr auto',
								},
								gridTemplateAreas: {
									xs: '"date when" "theme theme"',
									md: '"date theme when"',
								},
								alignItems: 'baseline',
								columnGap: { xs: 1, sm: 2 },
								rowGap: 0.25,
								width: '100%',
								minWidth: 0,
								pr: 1,
							}}
						>
							<Typography sx={{ gridArea: 'date', fontWeight: 600 }}>
								{formatSessionRange(session)}
							</Typography>
							<Typography
								sx={{
									gridArea: 'theme',
									minWidth: 0,
									overflowWrap: 'anywhere',
									typography: { xs: 'body2', sm: 'body1' },
								}}
							>
								{session.themeName}
							</Typography>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ gridArea: 'when', whiteSpace: 'nowrap', textAlign: 'right' }}
							>
								{countdown(session)}
							</Typography>
						</Box>
					</AccordionSummary>

					<AccordionDetails
						sx={{
							p: 0,
							borderTop: 1,
							borderColor: 'divider',
							'& > .MuiPaper-root': { boxShadow: 'none', borderRadius: 0 },
						}}
					>
						<ParticipantsGrid
							session={session}
							subscribersListId={subscribersListId}
							purchases={purchases}
						/>
					</AccordionDetails>
				</Accordion>
			))}
		</Box>
	);
}

export default ComingUpSessions;
